import type { AwardBodySummary } from './awards'

interface Props {
  bodies: AwardBodySummary[]
  onOpen: (wikidataId: string) => void
}

/**
 * Desktop parity: "Academy Awards", "BAFTA Awards" etc. — each body is
 * a tall gradient card with an oversized icon behind the name. Maps 1:1
 * to the desktop `BODY_STYLES` table in
 * `src/renderer/components/views/AwardsView.tsx`.
 */
const BODY_STYLES: Record<
  string,
  { icon: string; gradient: string; accent: string }
> = {
  'Academy Awards': {
    icon: '\uD83C\uDFC6',
    gradient: 'from-yellow-900/40 to-amber-950/60',
    accent: 'text-amber-300',
  },
  'BAFTA Awards': {
    icon: '\uD83C\uDFAD',
    gradient: 'from-purple-900/40 to-indigo-950/60',
    accent: 'text-purple-300',
  },
  'Golden Globe Awards': {
    icon: '\uD83C\uDF0D',
    gradient: 'from-yellow-800/40 to-orange-950/60',
    accent: 'text-yellow-300',
  },
  'Cannes Film Festival': {
    icon: '\uD83C\uDF34',
    gradient: 'from-sky-900/40 to-cyan-950/60',
    accent: 'text-sky-300',
  },
  'Venice Film Festival': {
    icon: '\uD83E\uDD81',
    gradient: 'from-red-900/40 to-rose-950/60',
    accent: 'text-rose-300',
  },
  'Berlin International Film Festival': {
    icon: '\uD83D\uDC3B',
    gradient: 'from-slate-700/40 to-zinc-900/60',
    accent: 'text-slate-200',
  },
  'Golden Raspberry Awards': {
    icon: '\uD83C\uDF47',
    gradient: 'from-pink-800/40 to-pink-950/60',
    accent: 'text-pink-300',
  },
  'Screen Actors Guild Awards': {
    icon: '\uD83C\uDFAC',
    gradient: 'from-emerald-900/40 to-teal-950/60',
    accent: 'text-emerald-300',
  },
  'Critics Choice Awards': {
    icon: '\u2B50',
    gradient: 'from-blue-900/40 to-indigo-950/60',
    accent: 'text-blue-300',
  },
}

function styleFor(name: string) {
  return (
    BODY_STYLES[name] ?? {
      icon: '\uD83C\uDFC6',
      gradient: 'from-bg-secondary to-bg-tertiary',
      accent: 'text-accent-gold',
    }
  )
}

export function BodyPicker({ bodies, onOpen }: Props) {
  if (bodies.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border-primary bg-bg-secondary/40 p-10 text-center text-sm text-text-tertiary">
        No award bodies available. The awards snapshot may still be downloading.
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
      {bodies.map((body) => {
        const style = styleFor(body.name)
        return (
          <button
            key={body.wikidataId}
            type="button"
            onClick={() => onOpen(body.wikidataId)}
            className={`group relative flex h-48 overflow-hidden rounded-2xl border border-white/5 bg-gradient-to-br text-left shadow-cinematic transition-all duration-200 motion-safe:hover:-translate-y-1 hover:border-accent-primary/40 hover:shadow-2xl ${style.gradient}`}
          >
            {/* Oversized background icon */}
            <span
              className="pointer-events-none absolute -right-6 -bottom-6 select-none text-[9rem] leading-none opacity-10 transition-opacity group-hover:opacity-20"
              aria-hidden
            >
              {style.icon}
            </span>

            <div className="relative z-10 flex flex-1 flex-col justify-between p-5">
              <div className="flex items-start justify-between">
                <span
                  className="rounded-full bg-black/40 p-2 text-2xl backdrop-blur-md transition-transform motion-safe:group-hover:scale-110"
                  aria-hidden
                >
                  {style.icon}
                </span>
                {body.country && (
                  <span className="rounded-md bg-black/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-text-secondary backdrop-blur-md">
                    {body.country}
                  </span>
                )}
              </div>

              <div>
                <h3
                  className={`text-xl font-bold leading-tight text-white transition-colors group-hover:text-accent-primary`}
                >
                  {body.name}
                </h3>
                <p className="mt-1.5 flex items-center gap-2 text-[11px] text-text-tertiary">
                  <span className="font-semibold">{body.editionCount}</span>
                  <span>ceremonies</span>
                  <span className="opacity-60">·</span>
                  <span className={`font-semibold ${style.accent}`}>
                    {body.latestYear ?? '—'}
                  </span>
                </p>
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}
